import React, { useMemo } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import Skeleton from "react-loading-skeleton";
import {
  buildCatalogPath,
  getCatalogVendeur,
  isCatalogPath,
} from "../../catalog/catalogTenant";
import {
  useInfiniteProductList,
  useInfiniteScrollSentinel,
} from "../../utils/hooks";
import { QueryUrl } from "../../utils/Utils";
import { BestSellerCard } from "../HomePage/BestSellerCard";
import FilterItems from "../user/ParcoursAchat/FilterItems";
import { ProductItemsSkeleton } from "../user/ParcoursAchat/ParcoursAchat";
import TemplateNavbar from "../shared/TemplateNavbar/TemplateNavbar";
import ProductCard from "../TemplateCard/ProductCard";

const LIMIT = 9;

const CatalogProductSkeleton = () => (
  <>
    {Array(6)
      .fill(null)
      .map((_, i) => (
        <div key={i} className="product-card">
          <div className="product-card__image-wrapper">
            <Skeleton className="product-card__image" height={160} />
          </div>
          <div className="product-card__body">
            <Skeleton width="70%" height={14} style={{ marginBottom: 6 }} />
            <Skeleton width="50%" height={14} style={{ marginBottom: 10 }} />
            <Skeleton width="100%" height={32} />
          </div>
        </div>
      ))}
  </>
);

function OurProduct() {
  const navigate = useNavigate();
  const isCatalog = isCatalogPath();
  const catalogVendeur = getCatalogVendeur();
  const [searchParams, setSearchParams] = useSearchParams();

  const [query, setQuery] = React.useState({
    categorie: searchParams?.get("categorie") || "",
    sous_categorie: searchParams?.get("sous_categorie") || "",
    prix_min: searchParams?.get("prix_min") || "",
    prix_max: searchParams?.get("prix_max") || "",
    orderBy: searchParams?.get("orderBy") || "",
  });

  const filtersKey =
    (searchParams?.get("categorie") ?? "") +
    (searchParams?.get("sous_categorie") ?? "") +
    (searchParams?.get("prix_min") ?? "") +
    (searchParams?.get("prix_max") ?? "");

  const listQuery = useMemo(
    () => ({
      limit: LIMIT,
      prix_min: searchParams?.get("prix_min") || "",
      prix_max: searchParams?.get("prix_max") || "",
      categorie: searchParams?.get("categorie") || "",
      sous_categorie: (() => {
        const raw = searchParams?.get("sous_categorie");
        if (!raw) return "";
        try {
          return JSON.parse(raw);
        } catch {
          return "";
        }
      })(),
    }),
    [
      searchParams?.get("prix_min"),
      searchParams?.get("prix_max"),
      searchParams?.get("categorie"),
      searchParams?.get("sous_categorie"),
    ],
  );

  const {
    allProducts,
    displayProducts,
    hasMore,
    isLoading,
    isFetchingMore,
    loadMore,
  } = useInfiniteProductList(listQuery, filtersKey);

  const sentinelRef = useInfiniteScrollSentinel(
    loadMore,
    hasMore,
    displayProducts.length,
  );

  const noopSetPage = React.useCallback((_page: number) => {}, []);

  const priceExtremes = React.useMemo(() => {
    if (!allProducts.length) return null;
    let min = Number.POSITIVE_INFINITY;
    let max = 0;
    for (const p of allProducts) {
      const raw = (p as any)?.prix ?? (p as any)?.price ?? null;
      const price = raw != null ? Number(raw) : NaN;
      if (!Number.isFinite(price)) continue;
      if (price < min) min = price;
      if (price > max) max = price;
    }
    if (!Number.isFinite(min)) return null;
    return { min, max };
  }, [allProducts]);

  const handleCatalogCardClick = (product: { slug?: string }) => {
    navigate(buildCatalogPath(`/produit/${product?.slug}`));
  };

  // Catalogue : retour accueil (catatégories déjà présentes), pas la page /produits
  const allProductsLink = isCatalog ? buildCatalogPath("") : "/produits";

  const backToAllProductsLink = searchParams.get("categorie") && (
    <div className="d-flex justify-content-center mt-2 mb-3">
      <Link
        to={allProductsLink}
        className="btn btn-purple btn-sm d-flex align-items-center gap-2 px-3 py-2 mt-3"
        onClick={() => {
          setSearchParams(
            QueryUrl("", {
              ...query,
              categorie: "",
              sous_categorie: [],
            })
          );
        }}
      >
        <i className="bi bi-arrow-left" />
        Tous les produits
      </Link>
    </div>
  );

  const infiniteScrollBlock = (
    <>
      <div
        ref={sentinelRef}
        className="infinite-scroll-sentinel"
        aria-hidden="true"
      />
      {isFetchingMore && hasMore && (
        <div className="d-flex justify-content-center py-3">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Chargement...</span>
          </div>
        </div>
      )}
    </>
  );

  if (isCatalog) {
    const storeName =
      catalogVendeur?.nom_boutique ||
      catalogVendeur?.nom_de_la_boutique ||
      "Nom de la boutique";

    return (
      <div className="template-wrapper">
        <TemplateNavbar vendeurName={storeName} />
        <div className="main-content">
          <h2 className="section-title catalog-products-title">
            Tous les produits
          </h2>

          <div className="template-products-col">
            {backToAllProductsLink}

            <div className="products-grid">
              {isLoading && displayProducts.length === 0 ? (
                <CatalogProductSkeleton />
              ) : displayProducts.length > 0 ? (
                displayProducts.map((product, index) => (
                  <ProductCard
                    key={
                      product?.id != null
                        ? `product-${product.id}-${index}`
                        : `product-fallback-${index}`
                    }
                    item={product}
                    devise={catalogVendeur?.devise}
                    onCardClick={handleCatalogCardClick}
                  />
                ))
              ) : null}
            </div>

            {!isLoading && displayProducts.length === 0 && (
              <p className="empty-message text-center">
                Aucun produit disponible
              </p>
            )}

            {infiniteScrollBlock}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="landing-page-component">
      <TemplateNavbar />
      <div className="page-content">
        <div className="container-easy-market">
          <div className="row row-achat-page mb-5">
            <FilterItems
              searchParams={searchParams}
              setSearchParams={setSearchParams}
              query={query}
              setQuery={setQuery}
              setPage={noopSetPage}
              minAvailablePrice={priceExtremes?.min}
              maxAvailablePrice={priceExtremes?.max}
            />
            <div className="col-lg-9 col-md-7 col-right-achat-page">
              <div className="content-col-right-achat-page">
                <div className="container-home-page mb-3 our-product-container-homepage our-product-grid-3 best-sellers-section">
                  <div className="best-sellers-section-container mt-3">
                    {backToAllProductsLink}

                    <div className="best-sellers-section-grid our-product-grid">
                      {isLoading && displayProducts.length === 0 && (
                        <ProductItemsSkeleton />
                      )}
                      {displayProducts.length > 0 &&
                        displayProducts.map((product, index) => (
                          <div
                            key={
                              product?.id != null
                                ? `product-${product.id}-${index}`
                                : `product-fallback-${index}`
                            }
                            className="best-sellers-section-grid-item"
                          >
                            <BestSellerCard item={product} />
                          </div>
                        ))}
                    </div>

                    {!isLoading && displayProducts.length === 0 && (
                      <div className="col-sm-12 col-md-12 mb-3 m-t-36 d-flex justify-content-center">
                        <p>Aucun produit disponible</p>
                      </div>
                    )}

                    {infiniteScrollBlock}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default OurProduct;
